import { useState, useEffect, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import { isEmpty } from 'lodash';

import { product_item_availability } from 'commons/constants';
import { getProductUrl } from 'commons/productUtils';
import getVisitorLocationQueryVariables from 'commons/getVisitorLocationQueryVariables';
import useSuspenseQuery from 'commons/useSuspenseQuery';
import usePdpSlug from 'components/consumer/PDP/state/context/usePdpSlug';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import usePdpSelectedOptionsVariantSku from 'components/consumer/PDP/state/pdpSelectedOptionsVariantSku/usePdpSelectedOptionsVariantSku';
import useUserLocation from 'global-state/userLocation/useUserLocation';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import usePDPClearanceData from 'data-hooks/useClearanceData/usePDPClearanceData';
import { PRODUCT_LOCAL_STOCK, GET_UPSELL_PRODUCT } from '../queries';
import { getPriceDetailsUtil } from '../utils';
import { getOptionAdditionalPrice } from '../useProductPrice/utils';
import { getConfigPhotosForSelectedOptions } from '../usePDPPhotos/utils';

const getOptionPrice = (options, selection) => {
  if (!options) {
    return getOptionAdditionalPrice(undefined, null, selection);
  }

  const finalOptions = options.map(option => {
    const option_values = option?.values?.[0]?.option_values || [];

    return {
      ...option,
      values: option_values,
    };
  });

  return getOptionAdditionalPrice(finalOptions, null, selection);
};

const usePDPUpSellCart = () => {
  // hooks
  const pdpSlug = usePdpSlug();
  const { data: basicData } = usePDPBasicDetails();
  const { data: optionsData } = usePDPOptions();
  const { data: clearanceData } = usePDPClearanceData();
  const userLocation = useUserLocation();

  // state
  const [priceDetails, setPriceDetails] = useState({});
  const [hideUpSell, setHideUpSell] = useState(true);
  const [isOutOfStock, setIsOutOfStock] = useState(false);
  const [upSellImgUrl, setUpSellImageUrl] = useState(null);
  const [upSellImgFallbackUrl, setUpSellImageFallbackUrl] = useState(null);
  const [upSellProductLink, setUpSellProductLink] = useState(null);

  // queries
  const { data: upSellProductData } = useSuspenseQuery(GET_UPSELL_PRODUCT, {
    variables: {
      slug: pdpSlug,
      ...getVisitorLocationQueryVariables(userLocation?.stateShortName),
    },
    skip: !pdpSlug,
  });

  const [getProductLocalStock, { data: localStockData }] = useLazyQuery(
    PRODUCT_LOCAL_STOCK
  );

  // variables
  const { selectedOptions } = optionsData || {};

  const promotion = useMemo(() => {
    if (!clearanceData?.isClearance) {
      return {};
    }

    return basicData?.meta?.promotion;
  }, [basicData?.meta, clearanceData?.isClearance]);

  const upSellProduct = upSellProductData?.getUpSellProduct?.upsell_product;
  const upSellProductOptions = upSellProduct?.product?.options;

  const optionPrice = useMemo(
    () => getOptionPrice(upSellProductOptions, selectedOptions),
    [upSellProductOptions, selectedOptions]
  );

  const productUrlKey = useMemo(() => {
    if (!upSellProductData?.getUpSellProduct?.upsell_product?.product?.slug) {
      return '';
    }
    return getProductUrl(
      upSellProductData?.getUpSellProduct?.upsell_product.product
    );
  }, [upSellProductData?.getUpSellProduct?.upsell_product]);

  const localQSStock = localStockData?.productLocalStock?.qs_stock;
  const selectedOptionsVariantSku = usePdpSelectedOptionsVariantSku();

  useEffect(() => {
    if (!upSellProduct?.product) {
      return;
    }

    if (!selectedOptionsVariantSku) {
      setHideUpSell(false);
      return;
    }

    // check if upsell product has the current selected options
    let hide = false;
    let selectionUrl = '';

    if (optionsData.selectedOptions && upSellProduct.product.options?.length) {
      Object.entries(optionsData.selectedOptions).forEach(
        ([optionId, optionValueId]) => {
          const option = upSellProduct.product.options.find(
            o => o.id === Number(optionId)
          );

          if (option?.values?.[0]?.option_values?.length) {
            selectionUrl += `${
              selectionUrl?.length ? '&' : ''
            }${option.name.toLowerCase()}=`;

            const optionValue = option.values[0].option_values.find(
              o => o.id === Number(optionValueId)
            );

            // if selected option value is not found, then hide it
            if (!optionValue) {
              hide = hide || true;
            } else {
              selectionUrl += `${optionValue.value
                .split(' ')
                .join('-')
                .toLowerCase()}`;
            }
          }
        }
      );
    }

    setHideUpSell(hide);

    if (!hide && productUrlKey) {
      setUpSellProductLink(
        selectionUrl ? `${productUrlKey}?${selectionUrl}` : productUrlKey
      );
    }

    if (upSellProduct?.upsell_image) {
      setUpSellImageUrl(upSellProduct.upsell_image);
    } else {
      const { selectedOptionsExtended, upholsteryOptionId } = optionsData;

      const images = getConfigPhotosForSelectedOptions(
        selectedOptionsVariantSku,
        selectedOptionsExtended,
        upholsteryOptionId,
        upSellProduct.product
      );

      setUpSellImageUrl(
        images[0]?.src || upSellProduct?.product?.images?.[0]?.url
      );

      setUpSellImageFallbackUrl(upSellProduct?.product?.images?.[0]?.url);
    }

    const priceData = getPriceDetailsUtil(
      upSellProduct?.product?.meta?.price,
      promotion,
      optionPrice
    );

    setPriceDetails(priceData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptionsVariantSku, upSellProduct?.product, productUrlKey]);

  useEffect(() => {
    if (!upSellProduct?.product) {
      return;
    }

    const { product } = upSellProduct;
    let outOfStock = false;

    if (product.attributes?.length) {
      const availability = product.attributes.find(
        a => a.code === 'item_availability'
      );

      if (availability) {
        outOfStock =
          `${availability.value}` ===
            `${product_item_availability.out_of_stock}` ||
          `${availability.value}` === `${product_item_availability.disabled}`;
      }
    }

    if (product.efg_flag && !product.is_external) {
      getProductLocalStock({
        variables: {
          id: product.id,
          zipCode: userLocation?.zip ? userLocation.zip.toString() : '',
          fromCache: true,
        },
        context: { noBatch: true },
      });
    }

    setIsOutOfStock(outOfStock);
  }, [
    upSellProduct?.product,
    userLocation,
    getProductLocalStock,
    upSellProduct,
  ]);

  useEffect(() => {
    if (
      localQSStock &&
      !Array.isArray(localQSStock) &&
      isEmpty(localQSStock.stock)
    ) {
      setIsOutOfStock(true);
    }
  }, [localQSStock]);

  return {
    data: {
      hideUpSell: isOutOfStock || hideUpSell,
      optionPrice,
      priceDetails,
      upSellImgFallbackUrl,
      upSellImgUrl,
      upSellProduct,
      upSellProductLink,
    },
  };
};

export default usePDPUpSellCart;
